import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Disclaimer } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import {
  SUBMISSION_STATUSES, READY_STATUSES, RESULT_STATUSES, DEAL_STATUSES, DOCUMENT_PACKET_STATUSES,
} from "@/lib/constants";
import { formatDurationMMSS } from "@/lib/duration";
import { ArrowLeft, Save, CheckCircle2, AlertTriangle, Music, Gavel, ShieldAlert, ExternalLink, Sparkles } from "lucide-react";
import { useAuth, canWrite } from "@/lib/auth";
import { toast } from "sonner";
import { DeleteRecordButton } from "@/components/DeleteRecordButton";
import { submissionDeleteEligibility } from "@/lib/deleteEligibility";

// MP7 — Submission editor / detail.
// Sections: A. Request Link · B. Track Selection · C. Submission Details.
// Plus an always-visible Submission Package Summary card driven by the selected track.
// Includes explicit Mark Ready to Send action gated by the linked track's clearance.

const BLANK = {
  // A. Request Link
  request_id: "", requester_name: "", company: "", project_title: "",
  // B. Track Selection
  selected_track_id: "", selected_track_title: "",
  // C. Submission Details
  date_prepared: "", prepared_by: "",
  submission_link: "",
  document_packet_status: "Not Started",
  submission_status: "Draft",
  result_status: "",
  deal_status: "",
  notes: "",
};

const UNREADY_STATUSES = new Set([
  "Incomplete", "Needs Review", "Attorney Review Required", "Restricted", "Ineligible",
]);

export default function SubmissionEditor({ mode }) {
  const { id } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writeAccess = canWrite(user);
  const [data, setData] = useState({ ...BLANK });
  const [meta, setMeta] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tracks").then(r => setTracks(r.data || [])).catch(() => {});
    api.get("/license-requests").then(r => setRequests(r.data || [])).catch(() => {});
    if (mode === "edit" && id) {
      api.get(`/submissions/${id}`).then(r => { setData({ ...BLANK, ...r.data }); setMeta(r.data); })
        .catch(e => toast.error(errMsg(e)));
    } else {
      const trackId = search.get("track");
      const requestId = search.get("request");
      if (trackId) {
        api.get(`/tracks/${trackId}`).then(r => {
          setData(d => ({ ...d, selected_track_id: trackId, selected_track_title: r.data.track_title }));
        }).catch(() => {});
      }
      if (requestId) {
        api.get(`/license-requests/${requestId}`).then(r => {
          const lr = r.data;
          setData(d => ({
            ...d,
            request_id: lr.request_id || lr.id,
            requester_name: lr.requester_name || "",
            company: lr.requester_company || "",
            project_title: lr.project_title || "",
            selected_track_id: d.selected_track_id || lr.requested_track_id || "",
            selected_track_title: d.selected_track_title || lr.requested_track_title || "",
            notes: lr.notes || "",
          }));
        }).catch(() => {});
      }
    }
  }, [mode, id, search]);

  function setField(k, v) { setData(d => ({ ...d, [k]: v })); }

  const selectedTrack = useMemo(
    () => tracks.find(t => t.id === data.selected_track_id) || null,
    [tracks, data.selected_track_id],
  );
  const eligibleForReadyToSend = selectedTrack ? READY_STATUSES.includes(selectedTrack.clearance_status) : false;
  const linkedUnready = selectedTrack ? UNREADY_STATUSES.has(selectedTrack.clearance_status) : false;

  async function save(overrides) {
    setSaving(true);
    try {
      const t = selectedTrack;
      const payload = {
        ...data,
        ...(overrides || {}),
        selected_track_title: t ? t.track_title : data.selected_track_title,
      };
      if (mode === "new") {
        const r = await api.post("/submissions", payload);
        toast.success("Submission created");
        navigate(`/submissions/${r.data.id}`, { replace: true });
      } else {
        const r = await api.put(`/submissions/${id}`, payload);
        toast.success("Saved");
        setMeta(r.data);
        setData({ ...BLANK, ...r.data });
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  }

  async function markReadyToSend() {
    if (!eligibleForReadyToSend) {
      toast.error("Linked track is not Client-Ready or Corporate-Ready. Resolve compliance status before external submission.");
      return;
    }
    await save({ submission_status: "Ready to Send" });
  }
  async function markSent() { await save({ submission_status: "Sent" }); }

  const tabFocus = search.get("tab"); // "package" anchors to summary

  return (
    <div data-testid="submission-editor">
      <PageHeader
        title={mode === "new" ? "New Submission Draft" : (meta?.project_title || "Submission")}
        subtitle={meta ? <span className="font-mono text-xs">{meta.submission_id} · prepared {(meta.date_prepared || "").slice(0, 10)} by {meta.prepared_by || "—"}</span> : "Internal draft package"}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/submissions")} data-testid="submission-back-button"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
            {writeAccess && mode === "edit" && (
              <>
                <Button
                  variant="outline"
                  disabled={!selectedTrack}
                  onClick={() => window.location.assign(`/ai-review-notes?generate=${selectedTrack?.id || ""}&submission=${id || ""}`)}
                  data-testid="submission-generate-ai-note"
                  title={!selectedTrack ? "Pick a track first" : ""}
                >
                  <Sparkles className="w-4 h-4 mr-2" />Generate AI Review Note
                </Button>
                <Button
                  variant="outline"
                  disabled={!eligibleForReadyToSend || data.submission_status === "Ready to Send" || saving}
                  onClick={markReadyToSend}
                  data-testid="submission-mark-ready"
                  title={!eligibleForReadyToSend ? "Linked track must be Client/Corporate-Ready" : ""}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />Mark Ready to Send
                </Button>
                <Button
                  variant="outline"
                  disabled={data.submission_status === "Sent" || saving}
                  onClick={markSent}
                  data-testid="submission-mark-sent"
                >
                  Mark Sent
                </Button>
              </>
            )}
            {writeAccess ? (
              <Button onClick={() => save()} disabled={saving} data-testid="submission-save-button" style={{ background: "hsl(347 72% 22%)" }}>
                <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save"}
              </Button>
            ) : (
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="submission-readonly-badge">Read-only access</span>
            )}
          </>
        }
      />

      {/* Readiness warning — always visible when relevant */}
      {selectedTrack && linkedUnready && (
        <Card className="mb-4 border-[hsl(0_70%_42%)]/40" data-testid="submission-warning">
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: "hsl(0 70% 42%)" }} />
            <div className="text-sm">
              <span className="font-medium" style={{ color: "hsl(0 70% 42%)" }}>This track is not eligible for Ready to Send.</span>{" "}
              Resolve compliance status before external submission. Current clearance: <span className="font-mono">{selectedTrack.clearance_status}</span>.
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* A. Request Link */}
          <Section title="A. Request link" testid="section-request">
            <Field label="Linked license request">
              <Select value={data.request_id || "unset"} onValueChange={v => setField("request_id", v === "unset" ? "" : v)}>
                <SelectTrigger data-testid="submission-request-select"><SelectValue placeholder="Select a request (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">— None —</SelectItem>
                  {requests.map(r => <SelectItem key={r.id} value={r.request_id || r.id}>{r.request_id} — {r.project_title || "(untitled)"}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Project title"><Input value={data.project_title} onChange={e => setField("project_title", e.target.value)} data-testid="submission-project-title" /></Field>
            <Field label="Requester name"><Input value={data.requester_name} onChange={e => setField("requester_name", e.target.value)} data-testid="submission-requester-name" /></Field>
            <Field label="Company"><Input value={data.company} onChange={e => setField("company", e.target.value)} data-testid="submission-company" /></Field>
          </Section>

          {/* B. Track Selection */}
          <Section title="B. Track selection" testid="section-track">
            <Field full label="Selected track">
              <Select value={data.selected_track_id || "unset"} onValueChange={v => setField("selected_track_id", v === "unset" ? "" : v)}>
                <SelectTrigger data-testid="submission-track-select"><SelectValue placeholder="Select a track…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">— None —</SelectItem>
                  {tracks.map(t => <SelectItem key={t.id} value={t.id}>{t.track_title} — {t.artist} ({t.clearance_status})</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedTrack && (
                <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="submission-snapshot">
                  <span className="text-xs text-muted-foreground">Snapshot clearance:</span>
                  <StatusBadge status={selectedTrack.clearance_status} />
                  {eligibleForReadyToSend ? (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded status-client" data-testid="submission-eligible">
                      <CheckCircle2 className="w-3 h-3" />Eligible for Ready to Send
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded status-attorney" data-testid="submission-ineligible">
                      <AlertTriangle className="w-3 h-3" />Not eligible
                    </span>
                  )}
                </div>
              )}
            </Field>
          </Section>

          {/* C. Submission Details */}
          <Section title="C. Submission details" testid="section-details">
            <Field label="Date prepared">
              <Input value={(data.date_prepared || "").slice(0, 10)} onChange={e => setField("date_prepared", e.target.value)} placeholder="YYYY-MM-DD" data-testid="submission-date-prepared" />
            </Field>
            <Field label="Prepared by"><Input value={data.prepared_by} onChange={e => setField("prepared_by", e.target.value)} placeholder="auto-filled on create" data-testid="submission-prepared-by" /></Field>
            <Field full label="Submission link (placeholder — future controlled delivery)">
              <Input value={data.submission_link} onChange={e => setField("submission_link", e.target.value)} placeholder="Internal share link…" data-testid="submission-link" />
            </Field>
            <Field label="Document packet status">
              <Select value={data.document_packet_status || "Not Started"} onValueChange={v => setField("document_packet_status", v)}>
                <SelectTrigger data-testid="submission-packet-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOCUMENT_PACKET_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Submission status">
              <Select value={data.submission_status} onValueChange={v => setField("submission_status", v)}>
                <SelectTrigger data-testid="submission-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUBMISSION_STATUSES.map(s => (
                    <SelectItem
                      key={s} value={s}
                      disabled={s === "Ready to Send" && !eligibleForReadyToSend}
                    >
                      {s}{s === "Ready to Send" && !eligibleForReadyToSend ? " — needs Client/Corporate-Ready track" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Result status">
              <Select value={data.result_status || "unset"} onValueChange={v => setField("result_status", v === "unset" ? "" : v)}>
                <SelectTrigger data-testid="submission-result-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">—</SelectItem>
                  {RESULT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Deal status">
              <Select value={data.deal_status || "unset"} onValueChange={v => setField("deal_status", v === "unset" ? "" : v)}>
                <SelectTrigger data-testid="submission-deal-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">—</SelectItem>
                  {DEAL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field full label="Notes for reviewer"><Textarea rows={4} value={data.notes} onChange={e => setField("notes", e.target.value)} data-testid="submission-notes" /></Field>
            <div className="md:col-span-2 mt-1 p-3 rounded-md bg-secondary/40" data-testid="submission-timeline">
              <div className="text-xs text-muted-foreground mb-1">Status timeline</div>
              <div className="text-sm">{meta?.created_at ? <>Created {(meta.created_at || "").slice(0, 19).replace("T", " ")} · last updated {(meta.updated_at || "").slice(0, 19).replace("T", " ")}</> : "Created on save"}</div>
            </div>
          </Section>
        </div>

        {/* Submission Package Summary — right rail */}
        <div className="space-y-4" id="package-summary" data-testid="submission-package-summary">
          <Card className={tabFocus === "package" ? "ring-2 ring-[hsl(40_48%_32%)]/40" : ""}>
            <CardContent className="p-5">
              <div className="font-display text-base mb-3">Submission package summary</div>
              {!selectedTrack ? (
                <div className="text-sm text-muted-foreground">No track selected. Choose a track to populate the package summary.</div>
              ) : (
                <div className="space-y-3 text-sm">
                  <SummaryRow icon={Music} label="Track" value={`${selectedTrack.track_title || "(untitled)"} — ${selectedTrack.artist || "—"}`} />
                  <SummaryRow label="Catalog code" value={selectedTrack.catalog_code || "—"} mono />
                  <SummaryRow label="Genre" value={selectedTrack.genre || "—"} />
                  <SummaryRow label="Duration" value={<span className="font-mono">{formatDurationMMSS(selectedTrack.duration)}</span>} />
                  <SummaryRow label="Mood" value={(selectedTrack.mood_tags || []).join(", ") || "—"} />
                  <SummaryRow label="Best use" value={selectedTrack.best_use || "—"} />
                  <SummaryRow label="Clearance" value={<StatusBadge status={selectedTrack.clearance_status} />} />
                  <SummaryRow label="Document packet" value={data.document_packet_status || "Not Started"} />
                  <SummaryRow
                    icon={Gavel}
                    label="Attorney review"
                    value={selectedTrack.attorney_review_required
                      ? <span className="text-[hsl(0_70%_42%)]">Required — {selectedTrack.attorney_review_reason || "no reason given"}</span>
                      : <span className="text-muted-foreground">Not flagged</span>}
                  />
                  <SummaryRow
                    icon={ShieldAlert}
                    label="Risk flags"
                    value={(() => {
                      const flags = [];
                      if (selectedTrack.sample_or_interpolation_flag) flags.push("Sample / interpolation");
                      if (selectedTrack.ownership_dispute_flag) flags.push("Ownership dispute");
                      if (selectedTrack.estate_or_heirship_flag) flags.push("Estate / heirship");
                      if (selectedTrack.third_party_approval_required) flags.push("3rd-party approval");
                      if ((selectedTrack.missing_documents || []).length) flags.push(`${selectedTrack.missing_documents.length} missing doc${selectedTrack.missing_documents.length > 1 ? "s" : ""}`);
                      return flags.length === 0
                        ? <span className="text-muted-foreground">None</span>
                        : <ul className="list-disc list-inside text-xs">{flags.map(f => <li key={f}>{f}</li>)}</ul>;
                    })()}
                  />
                  <SummaryRow label="Notes" value={data.notes || <span className="text-muted-foreground">—</span>} />
                  <Button variant="outline" size="sm" className="w-full" onClick={() => navigate(`/tracks/${selectedTrack.id}`)} data-testid="summary-open-track">
                    Open track <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Disclaimer>
        Submission packages are internal workflow drafts · Compliance support only · Not legal advice · Human review required before external delivery.
      </Disclaimer>

      {/* MP11 Phase 2 — Danger zone: whole-record delete for a Submission.
          Backend enforces RBAC; client eligibility is a UI courtesy. */}
      {id && data?.id && (() => {
        const elig = submissionDeleteEligibility(data, user);
        return (
          <DeleteRecordButton
            recordType="submission"
            recordLabel="Submission"
            endpoint={`/submissions/${data.id}`}
            confirmToken={data.submission_id || (data.id || "").slice(0, 8).toUpperCase()}
            confirmTokenLabel="submission id"
            disabled={!elig.allowed}
            disabledReason={elig.reason}
            onDeleted={() => { navigate("/submissions"); }}
            testidPrefix="submission-delete"
            isSuperAdmin={user?.role === "super_admin"}
          />
        );
      })()}
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="font-display text-base mb-3">{title}</div>
        <div className="grid md:grid-cols-2 gap-3">{children}</div>
      </CardContent>
    </Card>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SummaryRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
    </div>
  );
}
